import { useContext, useState, useMemo, useEffect } from "react";
import usePage from "hooks/page.hook";
import { makeStyles } from "@material-ui/core";
import { translate } from "utils/i18n";
import { LiteralsContext } from "containers/shared/literals";
import SiteSearch360 from 'site-search-360-react';

const useStyles = makeStyles(() => ({
    container: {
        display: 'inline-flex',
        flex: 1,
        width: '100%',
    },
    input: {
        width: '100%',
        flex: 1,
        backgroundColor: 'white',
        height: 35,
        borderRadius: 5,
        paddingRight: 10,
        paddingLeft: 10
    },
    iconButton: {
        backgroundColor: '#0089c6',
        width: 56,
        padding: 5,
        borderRadius: 5,
        marginLeft: 10,
    }
}));

/** DOCUMENTACION
 *  https://docs.sitesearch360.com/can-i-use-site-search-360-with-react
 *  Prueba publish
 *  <script async src="https://js.sitesearch360.com/plugin/bundle/14008.js"></script> 
 **/


const PageSearch = () => {
    const [{ lang }] = usePage();
    const classes = useStyles();
    const terms = useContext(LiteralsContext);

    const [placeholder, setPlaceholder] = useState(() => translate("Global", "searchPlaceholder", terms));

    useEffect(() => {
        setPlaceholder(translate("Global", "searchPlaceholder", terms));
    }, [lang, terms]);

    const showAllResults = useMemo(() => translate("Global", "showAllResults", terms), [lang])
    const seeMore = useMemo(() => translate("Global", "seeMore", terms), [lang])
    const foundSearchResultsFor = useMemo(() => translate("Global", "foundSearchResultsFor", terms), [lang])
    const showingCountOfTotalResults = useMemo(() => translate("Global", "showingCountOfTotalResults", terms), [lang])
    const youRecentlySearchedFor = useMemo(() => translate("Global", "youRecentlySearchedFor", terms), [lang])
    const notFoundAnyMatches = useMemo(() => translate("Global", "notFoundAnyMatches", terms), [lang])
    const notFoundAnyMatches_description = useMemo(() => translate("Global", "notFoundAnyMatches_description", terms), [lang])

    return (
        <div className={classes.container}>
            <SiteSearch360
                siteId="www.atib.es"
                key={placeholder} // force refresh when placeholder changes
                applyStyling={true}
                alias="sitesearch"
                //https://docs.sitesearch360.com/installation-guide#advanced-configuration
                ss360Config={{
                    suggestions: {
                        show: true, // whether to show search suggestions, set to false to disable search suggestions
                        //trigger: undefined, // a suggestion overlay trigger
                        showImages: false, // show images in search suggestions
                        num: 6, // max. number of search suggestions to be shown,
                        linksOpenNewTab: true, // (dataset: any) => { console.log('linksOpenNewTab', dataset); return true},
                        // additional information to be shown in search suggestions
                        searchHistoryLabel: youRecentlySearchedFor, // the headline for search queries recently entered by user, displayed on empty search box focus
                        // maxSearchHistoryEntries: 2, // max. number of recent queries to display when the user focuses a search box, set to 0 to disable search history. If emptyQuerySuggestions is defined, search history gets disabled
                        viewAllLabel: showAllResults, // the label of a 'View All' button shown at the end of the suggestion list, default: undefined - no 'View All' button will be added
                    },
                    style: {
                        themeColor: "#1388c2",
                        accentColor: "#0089c6",
                        searchBox: {
                            innerWidth: '100%',
                            outerWidth: '100%',
                            border: {
                                radius: "5px",
                                color: "#DDDDDD"
                            },
                            icon: {
                                color: "#666666",
                            },
                            button: {
                                icon: {
                                    color: "#FFFFFF",
                                    image: "magnifier"
                                },
                                textColor: "#FFFFFF",
                                text: "Search"
                            },
                            text: {
                                size: "14px"
                            },
                            background: {
                                color: "#FFFFFF"
                            },
                        },
                        loaderType: "circle",
                        additionalCss: `
                            .ss360-custom-search .ss360-custom-search__searchbox {
                                width: 100%;
                                max-width: 100%;
                                display: flex;
                                justify-content: center
                            }
                            .result { 
                                display: flex; 
                                flex-direction: row; 
                                margin: 10px; 
                                box-shadow: none; 
                            }
                            .container-image { 
                                margin: 10px; 
                                self-align: center;
                            }
                            .image {
                                object-fit: contain; 
                                width: 150px; 
                                height: 150px
                            }
                            .custom-link { 
                                font-style: italic; 
                            }
                            .column { display: flex; flex-direction: column; flex: 1 }
                            .result__title { 
                                color: #004f84; 
                                font-weight: 300; 
                                font-size: 22px;
                                text-transform: uppercase;
                                margin-top: 5px; 
                            }
                            .result__snippet { 
                                margin: 0
                            }
                            .suggest { 
                                display: flex; 
                                flex-direction: row; 
                                justify-content: space-between;
                            }
                            .suggest__title { 
                                margin: 0; 
                                text-decoration: none;  
                                color: #004f84;
                            }
                        `
                    },
                    searchBox: {
                        inject: true,
                        injectTo: "div",
                        injectPosition: "pre",
                        placeholder: placeholder,
                        style: { outerWidth: '100%' }
                    },
                    results: {
                        embedConfig: {
                            contentBlock: "main"
                        },
                        fullScreenConfig: {},
                        integrationType: "layover",

                        // to customize search interface labels and captions; for EN/DE/FR/NL the UI can be auto-translated with the ss360Config.language setting
                        caption: `${foundSearchResultsFor}`,//'Found #COUNT# search results for "#QUERY#"', // the caption above the search results
                        moreResultsButton: seeMore, // HTML for the more results button, all results are shown if set to null
                        noResultsText: `
                            <div class="result">
                                <img class="image" src="data:image/jpeg;base64,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"/>
                                <div class="column">
                                    <h3 class="result__title">${notFoundAnyMatches}</h3>
                                    <p>${notFoundAnyMatches_description}</p>
                                </div>
                            </div>
                        `, // the text to show when no results are found
                        noResultsRedirect: undefined, // the full URL to redirect to when no results are found, e.g. 'https://mysite.com/no-results'
                        // result image settings                      
                        resultTemplate: {
                            template:
                                `<div class="result">
                                    <div class="container-image">
                                        {{#image}}
                                            <img class="image" src="{{image}}"/>
                                        {{/image}}
                                        {{!#image}}
                                            <img class="image" src="https://sede.atib.es/estaticos/img/logo-atib.png"/>
                                        {{/image}}
                                    </div>
                                    <div class="column">
                                        <h3 class="result__title">{{name}}</h3>
                                        <p class="result__snippet">{{{content}}}</p>
                                        <a class="custom-link" href="{{link}}" target="">${seeMore}</a>
                                    </div>
                                </div>`,
                            // preRenderCallback: (dataset:any) =>{
                            //     //console.log('preRenderCallback ========>',dataset)
                            //     console.log('preRenderCallback modified ========>',{...dataset, target: dataset.link.includes('sede.atib.') ? "_self" : "_blank"})
                            //     return undefined
                            // },
                            templateBuiltCallback: (template: any, result: any) => {
                                const out = template.replace(`target=""`, result.link.includes('sede.atib') ? `target="_self"` : `target="_blank"`)
                                return out;
                            },
                        },
                        pageDescriptionLabel: `${showingCountOfTotalResults}`, //'Showing #COUNT# of #TOTAL# results', // a pagination label shown above the 'See more' button, set to null or an empty string to hide the label
                        showCopyLinkToPositionButton: false, // whether to show a button to copy a link to the current position in the result set
                    },
                    queryTerm: {
                        highlightColor: "#1388c2",
                        highlightMatchedContent: true,
                        tokenize: true
                    },
                    language: "es",

                    //siteId: "www.atib.es",
                    //callbacks: { // see the Callbacks section on this documentation page for some additional details
                    //     suggestChange: undefined, // callback triggered after suggestion set is changed, takes boolean indicating whether suggestions are visible as argument and an array of retrieved data sets
                    //     redirect: (url:any) => console.log('redirect ', url) , // callback to handle search redirects, takes redirect URL as parameter, window.location.href is changed by default
                    //     preSearch: undefined, // a callback that is triggered before the search is executed, e.g. to catch empty queries
                    //     postSearch: undefined, // a callback that is triggered after the search results have been populated
                    //     preSuggest: undefined, // a callback that is triggered before the search suggest is executed, takes the query and search box reference as arguments
                    //     searchResult: undefined, // a callback that is triggered after the search is executed, e.g. to build your own result page from the response
                    //     closeLayer: undefined,
                    //     init: undefined, // function to call after initialization is complete
                    //     moreResults: undefined, // a callback to call when the 'Show More Results' button is clicked
                    //     resultImageError: undefined, // a callback to call for 404 images, should return false value (if the image should be hidden) or a new placeholder image URL, the result of this function has higher priority than the `results.hideResultsWithoutImage` handler, receives the result's DOM Node as first argument
                    //     suggestLine: undefined, // a callback called after a suggestion entry is created
                    //     resultLine: (suggets: any, node: any) => console.log('suggets', suggets, '   node:', node), // a callback called after a search result entry is created
                    //     navigationClick: undefined, // a callback called after a navigation item is clicked, receives the toggled result group name as argument
                    //     preRender: undefined, // a callback called before the search results are rendered (does not get called on 'Show More Results' button click), receives the (grouped) search results as argument, should return a modified search results object if the search results should be modified (e.g. to reorder the result groups), if the received search result object is directly modified, all of the changes are reflected in the search results
                    //     filterRendered: undefined, // a callback called after the filter options have been rendered
                    //     searchError: undefined, // a callback called if the search request fails (e.g. the user is offline, or the search API is not available)
                    //     imageLoaded: undefined, // a callback called every time an image has been loaded in the search results
                    //     queryModification: undefined, // a callback called before the query is execute, should return a modified query
                    //     resultsPreloaded: undefined, // a callback called after a results page has been loaded
                    //     suggestPostRender: undefined, // a callback called after the search suggestions have been rendered
                    //     suggestsLoaded: undefined, // a callback called after the search suggestions have been loaded, receives an array of suggestion data sets as argument
                    //    singleResultPreRenderCallback:  (result:any) => console.log('result', result),  // a callback called before a single search result is rendered, receives the result and its variants as arguments
                    //},
                }} />
        </div>
    )
}

export default PageSearch;
